import { inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { LoggingService, ToastService } from '@ld.consumer-portal/utils';
import { GlanceService, ShareScreenErrorCode } from './glance.service';

const SHARE_SCREEN_ERROR_KEYS: Record<ShareScreenErrorCode, string> = {
  localhostUnsupported: 'shareScreenErrorUnavailable',
  sdkUnavailable: 'shareScreenErrorUnavailable',
  sessionAlreadyActive: 'shareScreenErrorSessionActive',
  startFailed: 'shareScreenErrorStartFailed',
  cancelled: 'shareScreenErrorCancelled',
};

/**
 * Single owner of the "Share My Screen" interaction: starts a Glance co-browsing
 * session, tracks the pending state, and surfaces localized failures.
 */
@Injectable({
  providedIn: 'root',
})
export class ShareScreenService {
  private readonly glanceService = inject(GlanceService);
  private readonly loggingService = inject(LoggingService);
  private readonly toastService = inject(ToastService);
  private readonly translocoService = inject(TranslocoService);

  private readonly starting = signal(false);
  readonly isStarting = this.starting.asReadonly();

  async startSession(source: string): Promise<void> {
    if (this.starting()) {
      return;
    }

    this.starting.set(true);
    try {
      const result = await this.glanceService.startSessionAsync();
      if (!result.success) {
        this.loggingService.logWarning(`[${source}] Share screen failed: ${result.error ?? 'unknown error'}`);
        // 'cancelled' is a user-initiated decline of the session dialog, not
        // a failure — free the button without a failure toast.
        if (result.errorCode !== 'cancelled') {
          this.notifyFailure(result.errorCode);
        }
      }
    } catch (error) {
      this.loggingService.logException(error, `[${source}] Share screen threw while starting a session:`);
      this.notifyFailure('startFailed');
    } finally {
      this.starting.set(false);
    }
  }

  private notifyFailure(errorCode: ShareScreenErrorCode = 'startFailed'): void {
    this.toastService.warning(this.translocoService.translate(SHARE_SCREEN_ERROR_KEYS[errorCode]));
  }
}
